import { observer } from "mobx-react-lite";
import { useEffect, useState, type ReactNode } from "react";
import { useAppStores } from "../app-context";
import { EntityPhoto } from "../../ui/entity-photo";
import { Field } from "../../ui/field";
import { FileInput } from "../../ui/file-input";
import { OperationalIcon } from "../../ui/operational-icon";
import { mediaUrlForKey } from "../../platform/media-key";
import { profilePhotoMessages } from "./profile-photo.messages";
import "./profile-photo-editor.css";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

interface UploadedDocument {
  readonly id: string;
  readonly state: "QUARANTINED" | "REJECTED" | "RELEASED";
}

export const ProfilePhotoEditor = observer(function ProfilePhotoEditor(): ReactNode {
  const { api, locale, session } = useAppStores();
  const messages = profilePhotoMessages[locale.code];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => () => {
    if (previewUrl !== null) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);
  const identity = session.identity;
  const activeOrganizationUnit = session.activeOrganizationUnit;
  if (identity === null || identity.personId === null || activeOrganizationUnit === null) return null;

  const upload = async (file: File): Promise<void> => {
    setError(null);
    setNotice(null);
    if (!ACCEPTED_TYPES.has(file.type) || file.size > MAX_BYTES || file.size === 0) {
      setError(messages.invalidFile);
      return;
    }
    setBusy(true);
    const nextPreview = URL.createObjectURL(file);
    setPreviewUrl((current) => {
      if (current !== null) URL.revokeObjectURL(current);
      return nextPreview;
    });
    try {
      const uploaded = await api.postBinary<UploadedDocument>(
        "/v1/documents",
        await file.arrayBuffer(),
        {
          "x-authority-status": "OPERATOR_PROVIDED",
          "x-declared-mime-type": file.type,
          "x-file-name": file.name,
          "x-scope-id": activeOrganizationUnit.id,
          "x-scope-type": "ORGANIZATION_UNIT",
          "x-source-label": "self-profile-photo",
        },
      );
      const avatarKey = `document:${uploaded.id}`;
      await api.put("/v1/people/self/profile-photo", { avatarKey });
      session.setOwnAvatarKey(avatarKey);
      setNotice(messages.saved);
    } catch {
      setError(messages.unavailable);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="profile-photo-editor">
      <div className="profile-photo-editor__heading">
        <EntityPhoto
          fallback={<OperationalIcon name="users" />}
          label={messages.photoLabel(identity.displayName)}
          size="large"
          src={previewUrl ?? mediaUrlForKey(identity.avatarKey)}
        />
        <div><strong>{messages.title}</strong><small>{messages.fileHint}</small></div>
      </div>
      <Field label={messages.fileLabel}>
        <FileInput
          accept="image/jpeg,image/png,image/webp"
          capture="user"
          disabled={busy}
          onChange={(event) => {
            const input = event.currentTarget;
            const file = input.files?.[0];
            if (file !== undefined) void upload(file).finally(() => { input.value = ""; });
          }}
        />
      </Field>
      {busy ? <p role="status">{messages.uploading}</p> : null}
      {notice !== null ? <p className="notice" role="status">{notice}</p> : null}
      {error !== null ? <p className="error-banner" role="alert">{error}</p> : null}
    </section>
  );
});
